import { reportPlatformError } from "$root/api/error-reporting";
import { portfolioLabRepository } from "$root/functional-areas/portfolio-lab/repository";
import { parallelize, parallelizeWithAllSettled } from "$root/utils/promise";
import type { VirtualInvestmentResponse, VirtualInvestmentSourceType } from "@eu.mdotm.api/portfolio-lab";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useCallback } from "react";
import { chartsStale, computeOutcome, DEFAULT_LAB_AUM, isAbortError } from "../common";
import { runCompositionWrite, syncComposition } from "../components/composition-editor/persistence";
import { applyComputeEvent, applyComputeOutcome, invalidateColumnCharts, untilComputed } from "../compute";
import { deleteInvestments } from "../deletion";
import type { InvestmentPickerDialogProvider } from "../components";
import {
	type PortfolioLabContextProps,
	type PortfolioLabInvestmentItem,
	type PortfolioLabInvestmentType,
	PortfolioLabContext,
	portfolioLabAdapter,
} from "../ctx";
import { trackPortfolioLabSessionChange } from "../analytics";

export type ReloadInvestmentsMetricsOptions = {
	/**
	 * Only invalidate the chart queries of the columns this returns `true` for — no server-side recompute
	 * (see `reloadInvestmentCharts`).
	 */
	skipCompute?: (investment: PortfolioLabInvestmentItem) => boolean;
	/** Restrict the reload to columns whose charts are stale (see `chartsStale`). */
	onlyStale?: boolean;
	/**
	 * Compute from the composition already stored server-side instead of syncing the client's first.
	 * For callers whose state was seeded FROM the server and therefore has nothing new to push — the
	 * load-time recompute.
	 */
	skipCompositionPush?: boolean;
};

export function useInvestmentLab() {
	const ctx = useMandatoryContext(PortfolioLabContext);

	const createInvestments = useCallback(
		async (investments: InvestmentRequestedParams) => {
			const scenarioId = readAtom(ctx.selectedScenarioId$) || undefined;
			const virtualInvestments = await investmentPickerToVirtualInvestment(investments, scenarioId);
			const investmentsCtx = portfolioLabAdapter.virtualInvestment.toContext(virtualInvestments);
			writeAtom(ctx.investments$, (current) => [...current, ...investmentsCtx]);
			trackPortfolioLabSessionChange(ctx, "addColumn");
			return investmentsCtx;
		},
		[ctx],
	);

	const reloadInvestmentsMetrics = useCallback(
		async (
			investments: PortfolioLabInvestmentItem[],
			opt?: ReloadInvestmentsMetricsOptions & { awaitCompletion?: boolean },
		) => {
			// Capture only the target uuids; the live record for each is re-resolved by uuid when its write
			// or compute actually starts (never a snapshot). By then a captured investment object may be
			// stale, removed or re-created. Keeping only ids and re-resolving stops a compute firing at a dead
			// virtualUuid — the BE 404 ("virtual investment not found") this guards against.
			const targetUuids = investments.map((inv) => inv.virtualUuid);

			if (!opt?.awaitCompletion) {
				// Fire-and-forget, but never drop the rejection: a failed compute/reload (network / BE compute
				// error) would otherwise vanish silently — the chart just keeps its stale/blurred state with no
				// feedback. Stale-uuid 404s cannot reach here (every write and compute re-resolves the record
				// when it starts), so this surfaces only genuine reload failures.
				reloadInvestmentsChartsByScenario(targetUuids, ctx, opt).catch((error) => {
					reportPlatformError(error, "ERROR", "portfolio", { message: "Unable to reload lab charts" });
				});
				return;
			}
			await reloadInvestmentsChartsByScenario(targetUuids, ctx, opt);
		},
		[ctx],
	);

	const removeAllInvestments = useCallback((): Promise<void> => {
		// Clear the whole lab: drop every column from the atom optimistically, then delete every virtual
		// investment server-side through the shared `deleteInvestments` (which cancels each column's
		// in-flight compute so its DELETE isn't queued behind it, and toasts if any fails). Same
		// contract as the per-column `onRemove`, applied to every column at once — writes chained behind
		// each delete run-time-resolve to a now-absent item and no-op. Only the virtual (lab) copies are
		// deleted; the real investments are untouched. View-config (charts/layout/decimals) is left as-is.
		//
		// The promise is RETURNED, not fired and forgotten: the confirm dialog awaits it so it stays up
		// until the server has actually dropped every column. A reload or navigation while the DELETEs
		// were still in flight would kill them, and the "cleared" columns would come back on re-entry.
		const virtualUuids = readAtom(ctx.investments$).map((inv) => inv.virtualUuid);
		writeAtom(ctx.investments$, () => []);
		trackPortfolioLabSessionChange(ctx, "removeAllColumns");
		return deleteInvestments(ctx, virtualUuids);
	}, [ctx]);

	return { createInvestments, reloadInvestmentsMetrics, removeAllInvestments };
}

async function reloadInvestmentsChartsByScenario(
	targetUuids: string[],
	ctx: PortfolioLabContextProps,
	opt?: ReloadInvestmentsMetricsOptions,
) {
	const wanted = new Set(targetUuids);
	const live = readAtom(ctx.investments$).filter((inv) => wanted.has(inv.virtualUuid));
	// `onlyStale` restricts the reload to columns whose charts are stale — recomputing the fresh ones
	// would trigger server-side computes for nothing. Callers that must reload regardless (scenario
	// change, freshly created investments) omit it.
	const targets = opt?.onlyStale ? live.filter(chartsStale) : live;
	if (targets.length === 0) {
		return;
	}
	try {
		writeAtom(ctx.metricsStatus$, "calculating");
		const scenarioId = readAtom(ctx.selectedScenarioId$) || undefined;
		await parallelize(
			targets.map((target) => async () => {
				try {
					await reloadInvestmentCharts(ctx, target.virtualUuid, scenarioId, opt);
				} catch (error) {
					// A compute aborted because its column is being deleted is not a failure: the charts it
					// would have refreshed are going away with the column. Anything else propagates.
					if (!isAbortError(error)) {
						throw error;
					}
				}
			}),
		);
		// A queued compute finishes when its `lab-compute` event arrives (see useLabComputeEvents), so the
		// pass, and the "Update Charts" click awaiting it, lasts until its columns stop computing.
		await untilComputed(
			ctx,
			targets.map((target) => target.virtualUuid),
		);
		writeAtom(ctx.metricsStatus$, "success");
	} catch (error) {
		writeAtom(ctx.metricsStatus$, "error");
		throw error;
	}
}

type InvestmentRequestedParams = { type: PortfolioLabInvestmentType; items: InvestmentPickerDialogProvider[] };
async function investmentPickerToVirtualInvestment(
	investment: InvestmentRequestedParams,
	scenarioId?: string,
): Promise<VirtualInvestmentResponse[]> {
	const entityType = labTypeToCompositionEntityType[investment.type];
	const virtualInvestments = await parallelizeWithAllSettled(
		investment.items.map((entity) => async () => {
			return portfolioLabRepository.createVirtualInvestment({
				source: { type: entityType, uuid: entity.uuid },
				name: entity.name,
				scenarioId: scenarioId,
				totalValue: DEFAULT_LAB_AUM,
			});
		}),
	);

	return virtualInvestments.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
}

/**
 * Brings one column's charts up to date, or starts to: `/compute` queues the compute and answers with
 * its `computeId`, which goes on the column as `pendingCompute`, and the column finishes when the
 * matching `lab-compute` event arrives (`applyComputeEvent`). An answer that queued nothing is the
 * outcome itself and is applied at once.
 *
 * The compute runs off the column's write lane: the backend stores its result only if the revision it
 * read is still current, so it needs no ordering against the PUTs, and an edit made meanwhile leaves
 * without waiting for it. A superseded result leaves the column stale, which is what it is. The
 * request stays registered in `investmentComputes`, so a delete of the column can still abort it.
 */
async function reloadInvestmentCharts(
	ctx: PortfolioLabContextProps,
	virtualUuid: string,
	scenarioId?: string,
	opt?: ReloadInvestmentsMetricsOptions,
) {
	const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
	if (column != null && opt?.skipCompute?.(column)) {
		// Compute is what stores the scenario on the column, so the one path that skips it — the picker's
		// columns whose stored reports already serve the scenario — has to store it on its own.
		await runCompositionWrite(ctx, virtualUuid, (investment) =>
			portfolioLabRepository.updateVirtualInvestmentScenario(investment, scenarioId ?? null),
		);
		invalidateColumnCharts(virtualUuid, scenarioId ?? null);
		return;
	}
	// `entity.reports` is only ever (re)written by the compute endpoint — the composition PUT is a
	// pure persist — so a compute is what actually makes the charts match the composition. When the
	// sync fails, the server does not hold the composition on screen and a compute would describe
	// the wrong one; `syncComposition` has already surfaced the failure.
	if (!opt?.skipCompositionPush && !(await syncComposition(ctx, virtualUuid))) {
		return;
	}
	if (!readAtom(ctx.investments$).some((i) => i.virtualUuid === virtualUuid)) {
		return;
	}
	const answer = await ctx.investmentComputes.run(virtualUuid, (signal) =>
		portfolioLabRepository.computeMetrics(virtualUuid, scenarioId, signal),
	);
	const outcome = computeOutcome(answer);
	if (outcome != null) {
		applyComputeOutcome(ctx, virtualUuid, outcome, { requested: scenarioId ?? null, reports: scenarioId ?? null });
		return;
	}
	const { computeId } = answer;
	if (computeId == null) {
		throw new Error(`/compute queued ${virtualUuid} without a computeId`);
	}
	// A save landed since this compute read the composition, so the backend will not report it.
	const revisionNow = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid)?.compositionRevision;
	if (answer.compositionRevision != null && revisionNow != null && answer.compositionRevision < revisionNow) {
		return;
	}
	writeAtom(ctx.investments$, (items) =>
		items.map((i) =>
			i.virtualUuid === virtualUuid
				? {
						...i,
						pendingCompute: {
							computeId,
							queuedAt: Date.now(),
							revision: answer.compositionRevision ?? i.compositionRevision,
							scenarioId: scenarioId ?? null,
							timedOut: false,
						},
					}
				: i,
		),
	);
	const early = ctx.earlyComputeEvents.take(computeId);
	if (early != null) {
		applyComputeEvent(ctx, early);
	}
}

const labTypeToCompositionEntityType: Record<PortfolioLabInvestmentType, VirtualInvestmentSourceType> = {
	INVESTMENT: "PORTFOLIO",
	INVESTMENT_PROPOSAL: "INVESTMENT_PROPOSAL",
	BENCHMARK: "BENCHMARK",
	REFERENCE: "REFERENCE",
};
